import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, useMediaQuery, Button, Stack, CircularProgress } from "@mui/material";
import { useLocation, useSearchParams, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import ProductGallery from "../../Components/Product/ProductGallery";
import SizeSelector from "../../Components/Product/SizeSelector";
import ColorSelector from "../../Components/Product/ColorSelector";
import SimilarProductsCard from "../../Components/Product/SimilarProductsCard";
import Footer from "../../Components/Common/Footer";
import { logUserAction } from "../../utils/analytics";
import { appendUtmTags } from "../../utils/urlUtils";
import ProductAssistantButton from "../../Components/AssistantChat/ProductAssistantButton";
import { X, CheckCircle2, ShoppingBag, AlertCircle, ArrowLeft, Info } from "lucide-react";

const WhatsAppIcon = ({ size = 18, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const InstagramIcon = ({ size = 18, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const ProductPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { brandSlug, skuSlug } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = location.state?.searchQuery || searchParams.get("q") || "";
  const product = location.state?.product || {};
  const trackingContext = location.state?.trackingContext || {};

  const isDirectOnboardedRoute = Boolean(brandSlug && skuSlug);
  const [directLoading, setDirectLoading] = useState(isDirectOnboardedRoute);

  // If accessed directly via /onboardedbrands/:brandSlug/:skuSlug, fetch from backend
  useEffect(() => {
    if (brandSlug && skuSlug) {
      setDirectLoading(true);
      axios
        .get(`${config.backendUrl}/inventory/onboarded-product`, {
          params: { brand_slug: brandSlug, sku_slug: skuSlug },
        })
        .then((res) => {
          if (res.data?.success && res.data?.product) {
            // Replace /onboardedbrands/ in history with /product
            // Using replace: true ensures the browser Back button goes directly to the prior site,
            // never trapping the visitor in an infinite redirect loop.
            navigate("/product", {
              replace: true,
              state: {
                product: res.data.product,
                similarProducts: [res.data.product],
                fromOnboardedUrl: true,
              },
            });
          } else {
            navigate("/", { replace: true });
          }
        })
        .catch((err) => {
          console.error("Error loading direct product link:", err);
          navigate("/", { replace: true });
        })
        .finally(() => {
          setDirectLoading(false);
        });
    }
  }, [brandSlug, skuSlug, navigate]);

  // If accessed directly at /product with no state and no slugs, navigate home
  useEffect(() => {
    if (!brandSlug && !skuSlug && (!location.state?.product || Object.keys(location.state.product).length === 0)) {
      navigate("/", { replace: true });
    }
  }, [brandSlug, skuSlug, location.state, navigate]);

  const allVariants = product?.variants || [];
  const uniqueColors = [...new Set(allVariants.map(v => v.color).filter(Boolean))];

  const [selectedColor, setSelectedColor] = useState(uniqueColors.length > 0 ? uniqueColors[0] : null);

  useEffect(() => {
    if (uniqueColors.length > 0) {
      setSelectedColor(uniqueColors[0]);
    } else {
      setSelectedColor(null);
    }
  }, [location.key]);

  const sizesToDisplay = React.useMemo(() => {
    let filtered = allVariants;
    if (selectedColor) {
      filtered = allVariants.filter(v => v.color === selectedColor);
    }
    const seen = new Set();
    return filtered.filter(v => {
      const rawSize = v.size;
      if (rawSize == null) return false;
      const sizeStr = String(rawSize).trim().toLowerCase();
      if (sizeStr === "" || sizeStr === "n/a" || sizeStr === "null" || sizeStr === "undefined") return false;

      if (seen.has(v.size)) return false;
      seen.add(v.size);
      return true;
    });
  }, [allVariants, selectedColor]);

  const mdScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, [location.key]);

  // Check if current product belongs to an onboarded brand
  const isOnboarded = Boolean(
    product?.is_onboarded_brand === true ||
    (product?.url && product.url.includes("/onboardedbrands/"))
  );

  const [brandContact, setBrandContact] = useState(null);

  useEffect(() => {
    if (isOnboarded && product?.brand) {
      axios
        .get(`${config.backendUrl}/brand-auth/brand-contact`, {
          params: { name: product.brand },
        })
        .then((res) => {
          if (res.data?.success && res.data.exists) {
            setBrandContact({
              whatsapp_number: res.data.whatsapp_number,
              instagram_handle: res.data.instagram_handle,
            });
          }
        })
        .catch((err) => {
          console.error("Error fetching brand contact:", err);
        });
    }
  }, [isOnboarded, product?.brand]);

  // Buy options modal state: "closed" | "channels" | "order_form" | "order_success"
  const [buyModalView, setBuyModalView] = useState("closed");

  // Broadcast checkout open state so global floating elements (e.g. Ask Handpik) can hide
  useEffect(() => {
    const isOpen = buyModalView !== "closed";
    window.dispatchEvent(new CustomEvent("handpik:checkout_toggle", { detail: { isOpen } }));
    return () => {
      window.dispatchEvent(new CustomEvent("handpik:checkout_toggle", { detail: { isOpen: false } }));
    };
  }, [buyModalView]);

  // Direct Order form inputs
  const [orderName, setOrderName] = useState("");
  const [orderCountryCode, setOrderCountryCode] = useState("");
  const [orderPhoneNumber, setOrderPhoneNumber] = useState("");
  const [confirmedPhone, setConfirmedPhone] = useState("");
  const [orderAddress, setOrderAddress] = useState("");
  const [selectedOrderSize, setSelectedOrderSize] = useState("");
  const [selectedOrderColor, setSelectedOrderColor] = useState("");
  const [orderFormError, setOrderFormError] = useState("");

  const availableSizes = useMemo(() => {
    const raw = (product?.variants || [])
      .map((v) => v.size)
      .filter((s) => s && String(s).trim() && String(s).toLowerCase() !== "n/a");
    return [...new Set(raw)];
  }, [product?.variants]);

  const availableColors = useMemo(() => {
    const raw = (product?.variants || [])
      .map((v) => v.color)
      .filter((c) => c && String(c).trim() && String(c).toLowerCase() !== "n/a");
    return [...new Set(raw)];
  }, [product?.variants]);

  const handleOpenDirectOrder = () => {
    setOrderFormError("");
    setSelectedOrderSize(selectedOrderSize || availableSizes[0] || "");
    setSelectedOrderColor(selectedColor || selectedOrderColor || availableColors[0] || "");
    setBuyModalView("order_form");
  };

  const handleCheckout = () => {
    if (isOnboarded) {
      handleOpenDirectOrder();
    } else {
      if (product?.url) {
        const checkoutUrl = appendUtmTags(product.url);
        window.open(checkoutUrl, "_blank");
      }
      logUserAction({
        action: "checkout_redirect",
        data: {
          product_id: product?.product_id || "",
          product_title: product?.title || "",
          brand: product?.brand || "",
          product_price:
            product?.price?.discounted ||
            product?.price?.original ||
            null,
          currency: product?.price?.currency || null,
          product_image: product?.image_url || null,
          redirect_url: product?.url || null,
          channel: "website",
          ...trackingContext,
        },
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleWhatsAppOrder = () => {
    const rawPhone = brandContact?.whatsapp_number || "";
    const cleanPhone = rawPhone.replace(/[^0-9]/g, "");
    const price = product?.price?.discounted || product?.price?.original || "";
    const currency = product?.price?.currency || "PKR";
    const sku = product?.product_id || product?.sku || "";
    const formattedPrice = price ? `${currency} ${parseFloat(price).toLocaleString()}` : "N/A";

    const brandSlug = (product?.brand || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const skuSlug = String(sku).toLowerCase().replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-");
    const productUrl = product?.product_url || product?.url || (brandSlug && skuSlug ? `https://handpik.ai/onboardedbrands/${brandSlug}/${skuSlug}` : window.location.href);

    const msg = `Hello ${product?.brand || ""}, I found your product on Handpik.ai and I'm interested in ordering:\n${productUrl}\nSKU: ${sku}\nProduct Name: ${product?.title || ""}\nPrice: ${formattedPrice}`;

    if (cleanPhone) {
      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, "_blank");
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    }

    logUserAction({
      action: "checkout_redirect",
      data: {
        product_id: sku,
        product_title: product?.title || "",
        brand: product?.brand || "",
        product_price: product?.price?.discounted || product?.price?.original || null,
        currency: currency,
        product_image: product?.image_url || product?.images?.[0] || null,
        redirect_url: productUrl,
        product_url: productUrl,
        channel: "whatsapp",
        is_onboarded: true,
        ...trackingContext,
      },
      timestamp: new Date().toISOString(),
    });

    setBuyModalView("closed");
  };

  const handleInstagramContact = () => {
    const handle = (brandContact?.instagram_handle || "").replace(/^@/, "").trim();
    if (handle) {
      window.open(`https://ig.me/m/${handle}`, "_blank");
    } else {
      window.open("https://instagram.com", "_blank");
    }

    const sku = product?.product_id || product?.sku || "";
    const brandSlug = (product?.brand || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const skuSlug = String(sku).toLowerCase().replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-");
    const productUrl = product?.product_url || product?.url || (brandSlug && skuSlug ? `https://handpik.ai/onboardedbrands/${brandSlug}/${skuSlug}` : window.location.href);
    const igRedirectUrl = handle ? `https://ig.me/m/${handle}` : "https://instagram.com";

    logUserAction({
      action: "checkout_redirect",
      data: {
        product_id: sku,
        product_title: product?.title || "",
        brand: product?.brand || "",
        product_price: product?.price?.discounted || product?.price?.original || null,
        currency: product?.price?.currency || "PKR",
        product_image: product?.image_url || product?.images?.[0] || null,
        redirect_url: igRedirectUrl,
        product_url: productUrl,
        channel: "instagram",
        is_onboarded: true,
        ...trackingContext,
      },
      timestamp: new Date().toISOString(),
    });

    setBuyModalView("closed");
  };

  const handleConfirmDirectOrder = (e) => {
    e?.preventDefault();
    const cleanCode = orderCountryCode.replace(/[^0-9]/g, "").trim();
    const cleanNumber = orderPhoneNumber.replace(/[^0-9]/g, "").trim();

    if (!orderName.trim()) {
      setOrderFormError("Please enter your full name.");
      return;
    }
    if (!cleanCode) {
      setOrderFormError("Please enter your country code.");
      return;
    }
    if (!cleanNumber) {
      setOrderFormError("Please enter your phone / WhatsApp number.");
      return;
    }

    const fullPhone = `+${cleanCode}${cleanNumber}`;
    if (!/^\+[0-9]{7,18}$/.test(fullPhone)) {
      setOrderFormError("Invalid phone number");
      return;
    }

    if (!orderAddress.trim()) {
      setOrderFormError("Please enter your complete delivery address.");
      return;
    }
    if (availableSizes.length > 0 && !selectedOrderSize) {
      setOrderFormError("Please select a size.");
      return;
    }
    if (availableColors.length > 0 && !selectedOrderColor) {
      setOrderFormError("Please select a color.");
      return;
    }

    setOrderFormError("");
    setConfirmedPhone(fullPhone);

    const sku = product?.product_id || product?.sku || "";
    const brandSlug = (product?.brand || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const skuSlug = String(sku).toLowerCase().replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-");
    const productUrl = product?.product_url || product?.url || (brandSlug && skuSlug ? `https://handpik.ai/onboardedbrands/${brandSlug}/${skuSlug}` : window.location.href);

    logUserAction({
      action: "checkout_redirect",
      data: {
        product_id: sku,
        product_title: product?.title || "",
        brand: product?.brand || "",
        product_price: product?.price?.discounted || product?.price?.original || null,
        currency: product?.price?.currency || "PKR",
        product_image: product?.image_url || product?.images?.[0] || null,
        redirect_url: productUrl,
        product_url: productUrl,
        channel: "direct_order",
        is_onboarded: true,
        customer_name: orderName.trim(),
        customer_phone: fullPhone,
        customer_address: orderAddress.trim(),
        selected_size: selectedOrderSize || null,
        selected_color: selectedOrderColor || null,
        ...trackingContext,
      },
      timestamp: new Date().toISOString(),
    });

    setBuyModalView("order_loading");
    setTimeout(() => {
      setBuyModalView("order_success");
    }, 1200);
  };

  const ProductDetails = () => (
    <>
      <Typography
        sx={{
          fontSize: "13px",
          fontFamily: "Poppins-SemiBold",
          letterSpacing: "1px",
          color: "#999",
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        {product?.brand}
      </Typography>

      <Typography
        sx={{
          color: "#1a1a1a",
          fontFamily: "'Lora', serif",
          fontWeight: 600,
          fontSize: { xs: "24px", md: "36px" },
          lineHeight: 1.2,
          letterSpacing: "-0.5px",
          mb: 2,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {product?.title}
      </Typography>

      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography
            sx={{
              fontSize: { xs: "24px", md: "28px" },
              fontWeight: 800,
              color: (product?.price?.discounted && product?.price?.original !== product?.price?.discounted) ? "#FF5252" : "#1a1a1a",
              fontFamily: "Poppins-Bold",
            }}
          >
            {product?.price?.discounted || product?.price?.original}
            <Typography
              component="span"
              sx={{
                fontSize: { xs: "16px", md: "18px" },
                fontWeight: 600,
                color: (product?.price?.discounted && product?.price?.original !== product?.price?.discounted) ? "#FF5252" : "#666",
                ml: 0.5,
              }}
            >
              {product?.price?.currency}
            </Typography>
          </Typography>

          {product?.price?.discounted && product?.price?.original !== product?.price?.discounted && (
            <Typography
              sx={{
                fontSize: { xs: "16px", md: "18px" },
                color: "#999",
                fontFamily: "Poppins-Regular",
                textDecoration: "line-through",
              }}
            >
              {product?.price?.original}
            </Typography>
          )}


        </Stack>
      </Box>

      {uniqueColors.length > 0 && (
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <ColorSelector
            colors={uniqueColors}
            selectedColor={selectedColor}
            onSelectColor={setSelectedColor}
          />
        </Box>
      )}

      {sizesToDisplay.length > 0 && (
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <SizeSelector sizes={sizesToDisplay} />
        </Box>
      )}

      {!mdScreen && (
        isOnboarded ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Button
              fullWidth
              onClick={handleOpenDirectOrder}
              sx={{
                background: "#4F46E5",
                color: "#fff",
                fontFamily: "Poppins-SemiBold",
                fontSize: "15px",
                py: 1.75,
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                animation: "directOrderPulseRing 4.2s ease-out infinite",
                animationDelay: "0.5s",
                transition: "all 0.25s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                "&:hover": {
                  background: "#4338CA",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 22px rgba(79, 70, 229, 0.45)",
                  animation: "none",
                },
              }}
            >
              <ShoppingBag className="w-5 h-5 flex-shrink-0" />
              <span>Place Direct Order</span>
            </Button>

            <Button
              fullWidth
              onClick={handleWhatsAppOrder}
              sx={{
                background: "#25D366",
                color: "#fff",
                fontFamily: "Poppins-SemiBold",
                fontSize: "15px",
                py: 1.75,
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: "0 4px 14px rgba(37, 211, 102, 0.25)",
                animation: "whatsappPulseRing 4.2s ease-out infinite",
                animationDelay: "1.9s",
                transition: "all 0.25s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                "&:hover": {
                  background: "#20bd5a",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px rgba(37, 211, 102, 0.35)",
                  animation: "none",
                },
              }}
            >
              <WhatsAppIcon size={20} />
              <span>Order via WhatsApp</span>
            </Button>

            <Button
              fullWidth
              onClick={handleInstagramContact}
              sx={{
                background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                color: "#fff",
                fontFamily: "Poppins-SemiBold",
                fontSize: "15px",
                py: 1.75,
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: "0 4px 14px rgba(220, 39, 67, 0.25)",
                animation: "instagramPulseRing 4.2s ease-out infinite",
                animationDelay: "3.3s",
                transition: "all 0.25s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                "&:hover": {
                  opacity: 0.95,
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px rgba(220, 39, 67, 0.35)",
                  animation: "none",
                },
              }}
            >
              <InstagramIcon size={20} />
              <span>Order via Instagram</span>
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            onClick={handleCheckout}
            sx={{
              background: "linear-gradient(110deg, #FFB79C 0%, #FFA785 40%, #FFD4C2 50%, #FFA785 60%, #FFB79C 100%)",
              backgroundSize: "200% auto",
              color: "#fff",
              fontFamily: "Poppins-SemiBold",
              fontSize: "16px",
              py: 2,
              borderRadius: "12px",
              textTransform: "none",
              boxShadow: "0 8px 24px rgba(255,183,156,0.4)",
              animation: "buyPulseRing 1.5s ease-out infinite, buyShimmer 5s ease-in-out infinite",
              animationDelay: "1s, 0s",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 32px rgba(255,183,156,0.6)",
                animation: "none",
              }
            }}
          >
            Buy on {product?.brand}
          </Button>
        )
      )}

      {product?.description && product.description !== "N/A" && (
        <Box sx={{ mt: { xs: 4, md: 6 }, pt: 3, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <Typography
            sx={{
              fontFamily: "Poppins-SemiBold",
              fontSize: "14px",
              color: "#1a1a1a",
              mb: 1.5,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Description
          </Typography>
          <Box
            sx={{
              maxHeight: "30vh",
              overflowY: "auto",
              pr: 2,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "rgba(0,0,0,0.02)" },
              "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.1)", borderRadius: "4px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins-Regular",
                fontSize: "14px",
                color: "#666",
                lineHeight: 1.8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                ...(mdScreen && !descExpanded ? {
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                } : {})
              }}
            >
              {product?.description}
            </Typography>
            {mdScreen && (
              <Box
                onClick={() => setDescExpanded(!descExpanded)}
                sx={{
                  color: "#888",
                  fontFamily: "Poppins-SemiBold",
                  fontSize: "13px",
                  mt: 1.5,
                  cursor: "pointer",
                  display: "inline-block"
                }}
              >
                {descExpanded ? "Read less" : "Read more"}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );

  if (directLoading || (brandSlug && skuSlug)) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#FFFFFF",
        }}
      >
        <CircularProgress sx={{ color: "#FF8C38" }} />
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes pageFadeUp {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes buyPulseRing {
            0% { box-shadow: 0 0 0 0 rgba(255, 167, 133, 0.7), 0 4px 14px rgba(255,183,156,0.5); transform: scale(1); }
            15% { transform: scale(1.04); }
            30% { box-shadow: 0 0 0 16px rgba(255, 167, 133, 0), 0 4px 14px rgba(255,183,156,0.5); transform: scale(1); }
            100% { box-shadow: 0 0 0 0 rgba(255, 167, 133, 0), 0 4px 14px rgba(255,183,156,0.5); transform: scale(1); }
          }
          @keyframes directOrderPulseRing {
            0% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.7), 0 4px 14px rgba(79, 70, 229, 0.35); transform: scale(1); }
            14% { transform: scale(1.04); }
            28% { box-shadow: 0 0 0 14px rgba(79, 70, 229, 0), 0 4px 14px rgba(79, 70, 229, 0.35); transform: scale(1); }
            100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0), 0 4px 14px rgba(79, 70, 229, 0.35); transform: scale(1); }
          }
          @keyframes whatsappPulseRing {
            0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7), 0 4px 14px rgba(37, 211, 102, 0.25); transform: scale(1); }
            14% { transform: scale(1.04); }
            28% { box-shadow: 0 0 0 14px rgba(37, 211, 102, 0), 0 4px 14px rgba(37, 211, 102, 0.25); transform: scale(1); }
            100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0), 0 4px 14px rgba(37, 211, 102, 0.25); transform: scale(1); }
          }
          @keyframes instagramPulseRing {
            0% { box-shadow: 0 0 0 0 rgba(220, 39, 67, 0.7), 0 4px 14px rgba(220, 39, 67, 0.25); transform: scale(1); }
            14% { transform: scale(1.04); }
            28% { box-shadow: 0 0 0 14px rgba(220, 39, 67, 0), 0 4px 14px rgba(220, 39, 67, 0.25); transform: scale(1); }
            100% { box-shadow: 0 0 0 0 rgba(220, 39, 67, 0), 0 4px 14px rgba(220, 39, 67, 0.25); transform: scale(1); }
          }
          @keyframes buyShimmer {
            0% { background-position: 200% center; }
            100% { background-position: -200% center; }
          }
        `}
      </style>

      <Box
        key={location.key}
        sx={{
          backgroundColor: "#FFFFFF",
          minHeight: "100vh",
          animation: "pageFadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
      >

        {/* Desktop Layout */}
        {!mdScreen && (
          <Box sx={{ width: "100%", display: "flex", maxWidth: "1600px", mx: "auto" }}>
            {/* Left Column: Huge Gallery (Dictates Page Scroll) */}
            <Box sx={{
              width: "50%",
              minWidth: 0,
              p: 4,
              pt: 0,
              pl: { xs: 4, lg: 8 },
              position: "relative"
            }}>
              <ProductAssistantButton
                product={product}
                sx={{
                  left: "auto",
                  right: { xs: 16, md: 56 }, // matches back button inset (24px) + container padding (32px)
                  top: { xs: 16, md: 32 }, // matches back button inset (24px) + gallery margin (8px)
                  zIndex: 20,
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 1, md: 1.25 },
                  "& .MuiAvatar-root": {
                    width: { xs: 18, md: 20 },
                    height: { xs: 18, md: 20 },
                  },
                  "& .MuiTypography-root": {
                    fontSize: { xs: "12px", md: "13px" },
                  }
                }}
              />
              <ProductGallery
                productImages={product?.images}
                fallbackImage={product?.image_url}
              />
            </Box>

            {/* Right Column: Details */}
            <Box sx={{ width: "55%", minWidth: 0 }}>
              <Box
                sx={{
                  px: 6,
                  py: 10,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ProductDetails />
              </Box>
            </Box>
          </Box>
        )}

        {/* Mobile Layout */}
        {mdScreen && (
          <Box sx={{ pb: { xs: "130px", md: "100px" }, position: "relative" }}> {/* Padding for sticky bottom bar */}
            <ProductAssistantButton
              product={product}
              sx={{
                left: "auto",
                right: { xs: 16, md: 56 },
                top: { xs: 16, md: 32 },
                zIndex: 20,
                px: { xs: 1.5, md: 2 },
                py: { xs: 1, md: 1.25 },
                "& .MuiAvatar-root": {
                  width: { xs: 18, md: 20 },
                  height: { xs: 18, md: 20 },
                },
                "& .MuiTypography-root": {
                  fontSize: { xs: "12px", md: "13px" },
                }
              }}
            />
            <ProductGallery
              productImages={product?.images}
              fallbackImage={product?.image_url}
            />

            <Box
              sx={{
                mt: "-32px",
                position: "relative",
                zIndex: 10,
                backgroundColor: "#fff",
                borderRadius: "32px 32px 0 0",
                pt: 4,
              }}
            >
              <Box sx={{ px: 3 }}>
                <ProductDetails />
              </Box>

              <Box sx={{ mt: 4 }}>
                <SimilarProductsCard product={product} hideBrand={false} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Desktop Similar Products */}
        {!mdScreen && (
          <Box sx={{ maxWidth: "1600px", mx: "auto", px: 4, pt: 0, pb: 6, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <SimilarProductsCard product={product} hideBrand={false} />
          </Box>
        )}

        {!mdScreen && <Footer />}
      </Box>

      {/* Sticky Mobile Checkout Bar */}
      {mdScreen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(12px)",
            borderTop: "1px solid rgba(0,0,0,0.05)",
            p: 2,
            px: { xs: 2, sm: 3 },
            zIndex: 50,
            boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          }}
        >
          {isOnboarded ? (
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              {/* Row 1: Total & Price */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", width: "100%", mb: 1.25 }}>
                <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: "13px", color: "#666", textTransform: "uppercase" }}>
                  Total
                </Typography>
                <Typography sx={{ fontFamily: "Poppins-Bold", fontSize: "22px", color: "#1a1a1a", lineHeight: 1 }}>
                  {product?.price?.discounted || product?.price?.original}
                </Typography>
              </Box>

              {/* Row 2: 3 Action Buttons */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: { xs: 1, sm: 1.5 },
                  width: "100%",
                }}
              >
                {/* Option 1: Direct Order */}
                <Button
                  onClick={handleOpenDirectOrder}
                  sx={{
                    background: "#4F46E5",
                    color: "#fff",
                    fontFamily: "Poppins-SemiBold",
                    fontSize: { xs: "11px", sm: "13px" },
                    py: 1.4,
                    px: { xs: 0.5, sm: 1.5 },
                    minHeight: "44px",
                    borderRadius: "24px",
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                    animation: "directOrderPulseRing 4.2s ease-out infinite",
                    animationDelay: "0.5s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.5, sm: 0.75 },
                    whiteSpace: "nowrap",
                    "&:hover": {
                      background: "#4338CA",
                      animation: "none",
                    },
                  }}
                >
                  <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>Direct Order</span>
                </Button>

                {/* Option 2: WhatsApp */}
                <Button
                  onClick={handleWhatsAppOrder}
                  title="Order via WhatsApp"
                  sx={{
                    background: "#25D366",
                    color: "#fff",
                    fontFamily: "Poppins-SemiBold",
                    fontSize: { xs: "11px", sm: "13px" },
                    py: 1.4,
                    px: { xs: 0.5, sm: 1.5 },
                    minHeight: "44px",
                    borderRadius: "24px",
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(37, 211, 102, 0.25)",
                    animation: "whatsappPulseRing 4.2s ease-out infinite",
                    animationDelay: "1.9s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.5, sm: 0.75 },
                    whiteSpace: "nowrap",
                    "&:hover": {
                      background: "#20bd5a",
                      animation: "none",
                    },
                  }}
                >
                  <WhatsAppIcon size={16} />
                  <span>WhatsApp</span>
                </Button>

                {/* Option 3: Instagram */}
                <Button
                  onClick={handleInstagramContact}
                  title="Order via Instagram"
                  sx={{
                    background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                    color: "#fff",
                    fontFamily: "Poppins-SemiBold",
                    fontSize: { xs: "11px", sm: "13px" },
                    py: 1.4,
                    px: { xs: 0.5, sm: 1.5 },
                    minHeight: "44px",
                    borderRadius: "24px",
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(220, 39, 67, 0.25)",
                    animation: "instagramPulseRing 4.2s ease-out infinite",
                    animationDelay: "3.3s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.5, sm: 0.75 },
                    whiteSpace: "nowrap",
                    "&:hover": {
                      opacity: 0.95,
                      animation: "none",
                    },
                  }}
                >
                  <InstagramIcon size={15} />
                  <span>Instagram</span>
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Box sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: "13px", color: "#666", textTransform: "uppercase" }}>
                  Total
                </Typography>
                <Typography sx={{ fontFamily: "Poppins-Bold", fontSize: "22px", color: "#1a1a1a", lineHeight: 1 }}>
                  {product?.price?.discounted || product?.price?.original}
                </Typography>
              </Box>
              <Button
                onClick={handleCheckout}
                sx={{
                  background: "linear-gradient(110deg, #FFB79C 0%, #FFA785 40%, #FFD4C2 50%, #FFA785 60%, #FFB79C 100%)",
                  backgroundSize: "200% auto",
                  color: "#fff",
                  fontFamily: "Poppins-SemiBold",
                  fontSize: "14px",
                  px: 5,
                  py: 1.5,
                  minWidth: "140px",
                  borderRadius: "24px",
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(255,183,156,0.5)",
                  animation: "buyPulseRing 1.5s ease-out infinite, buyShimmer 5s ease-in-out infinite",
                  animationDelay: "1s, 0s",
                }}
              >
                Buy on {product?.brand}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Fullscreen Direct Checkout Page (Slides in from right) */}
      <div
        className={`fixed inset-0 z-[1050] bg-[#F8F9FA] overflow-y-auto transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${buyModalView !== "closed" ? "translate-x-0" : "translate-x-full pointer-events-none"
          }`}
      >
        {/* Checkout Top Bar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200/80 shadow-xs">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setBuyModalView("closed")}
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-700 hover:text-gray-950 p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Product</span>
            </button>

            <div className="text-center">
              <h1 className="text-base sm:text-lg font-bold text-gray-900 font-sans tracking-tight">Checkout</h1>
            </div>

            <div className="w-9 sm:w-28" />
          </div>
        </header>

        {/* VIEW 1: CHECKOUT FORM & SUMMARY */}
        {buyModalView === "order_form" && (
          <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
            <form onSubmit={handleConfirmDirectOrder} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Left 7 Columns: Checkout Details Form */}
              <div className="lg:col-span-7 space-y-6">

                {/* Customer Contact Info */}
                <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-200/80 shadow-xs">
                  <div className="pb-4 mb-4 border-b border-gray-100">
                    <h2 className="text-sm sm:text-base font-bold text-gray-900">Contact Information</h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your name"
                        value={orderName}
                        onChange={(e) => setOrderName(e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder:text-gray-400"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Phone / WhatsApp Number <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="relative flex items-center w-24 sm:w-28 flex-shrink-0">
                          <span className="absolute left-3 text-sm font-semibold text-gray-500 select-none">+</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="92"
                            maxLength={5}
                            value={orderCountryCode}
                            onChange={(e) => setOrderCountryCode(e.target.value.replace(/[^0-9]/g, ""))}
                            className="w-full border border-gray-300 rounded-xl py-2.5 pl-7 pr-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-center font-mono"
                            required
                          />
                        </div>
                        <input
                          type="tel"
                          inputMode="numeric"
                          placeholder="3001234567"
                          value={orderPhoneNumber}
                          onChange={(e) => setOrderPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))}
                          className="flex-1 border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono placeholder:text-gray-400"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-200/80 shadow-xs">
                  <div className="pb-4 mb-4 border-b border-gray-100">
                    <h2 className="text-sm sm:text-base font-bold text-gray-900">Delivery Address</h2>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Complete Delivery Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      placeholder="House / Apartment #, Street, Area, City"
                      value={orderAddress}
                      onChange={(e) => setOrderAddress(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder:text-gray-400 resize-none"
                      required
                    />
                  </div>
                </div>

              </div>

              {/* Right 5 Columns: Order Summary Card */}
              <div className="lg:col-span-5">
                <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-200/80 shadow-xs lg:sticky lg:top-24 space-y-5">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <h2 className="text-base font-bold text-gray-900">Order Summary</h2>
                    <span className="text-xs font-semibold text-gray-500">1 Item</span>
                  </div>

                  {/* Product Preview Card */}
                  <div className="flex gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    {product?.image_url || product?.images?.[0] ? (
                      <img
                        src={product?.image_url || product?.images?.[0]}
                        alt=""
                        referrerPolicy="no-referrer"
                        className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl border border-gray-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-xl flex items-center justify-center text-gray-400 flex-shrink-0">
                        <ShoppingBag className="w-8 h-8" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <span className="text-[11px] font-bold text-indigo-600 uppercase tracking-wider block">
                          {product?.brand}
                        </span>
                        <h3 className="font-semibold text-xs sm:text-sm text-gray-900 line-clamp-2 mt-0.5">
                          {product?.title}
                        </h3>
                        {(product?.product_id || product?.sku) && (
                          <span className="text-[10px] text-gray-600 font-mono block mt-1">
                            SKU: {product?.product_id || product?.sku}
                          </span>
                        )}
                      </div>

                      <div className="mt-2">
                        <span className="text-sm sm:text-base font-bold text-gray-900">
                          {product?.price?.currency || "PKR"} {parseFloat(product?.price?.discounted || product?.price?.original || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Size and Color Variant Chooser (if available) */}
                  {(availableSizes.length > 0 || availableColors.length > 0) && (
                    <div className="p-3.5 bg-gray-50/80 rounded-xl border border-gray-100 space-y-3">
                      {availableSizes.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-semibold text-gray-700">
                              Size <span className="text-red-500">*</span>
                            </label>
                            <span className="text-[11px] text-indigo-600 font-medium">{selectedOrderSize || "Choose"}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {availableSizes.map((s) => (
                              <button
                                key={s}
                                type="button"
                                onClick={() => setSelectedOrderSize(s)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${selectedOrderSize === s
                                  ? "bg-indigo-600 text-white shadow-xs"
                                  : "bg-white text-gray-700 border border-gray-200 hover:border-gray-400"
                                  }`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {availableColors.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-semibold text-gray-700">
                              Color <span className="text-red-500">*</span>
                            </label>
                            <span className="text-[11px] text-indigo-600 font-medium">{selectedOrderColor || "Choose"}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {availableColors.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setSelectedOrderColor(c)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${selectedOrderColor === c
                                  ? "bg-indigo-600 text-white shadow-xs"
                                  : "bg-white text-gray-700 border border-gray-200 hover:border-gray-400"
                                  }`}
                              >
                                {c}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Price / Total */}
                  <div className="border-t border-gray-100 text-xs sm:text-sm">
                    <div className="flex justify-between items-center text-base font-bold text-gray-900">
                      <span>Total</span>
                      <span className="text-indigo-600">
                        {product?.price?.currency || "PKR"} {parseFloat(product?.price?.discounted || product?.price?.original || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Brand Notice Message */}
                  <div className="p-1.5 bg-gray-50 rounded-xl border border-gray-200/80 text-[10px] sm:text-xs text-gray-600 flex items-center justify-center gap-1.5 sm:gap-2 leading-relaxed">
                    <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 shrink-0" />
                    <span>The brand will first contact you to verify and confirm your order</span>
                  </div>

                  {/* Form Errors */}
                  {orderFormError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-500" />
                      <span>{orderFormError}</span>
                    </div>
                  )}

                  {/* Place Order CTA */}
                  <button
                    type="submit"
                    className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white rounded-xl text-sm sm:text-base font-bold shadow-lg shadow-indigo-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Place Order</span>
                  </button>
                </div>
              </div>

            </form>
          </main>
        )}

        {/* VIEW 2: LOADING SPINNER */}
        {buyModalView === "order_loading" && (
          <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center px-4 py-16 text-center">
            <CircularProgress size={48} sx={{ color: "#4f46e5" }} thickness={4} />
            <p className="mt-5 text-sm sm:text-base font-semibold text-gray-800">
              Placing your order...
            </p>
          </div>
        )}

        {/* VIEW 3: ORDER SUCCESS CONFIRMATION */}
        {buyModalView === "order_success" && (
          <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center max-w-xl mx-auto px-4 py-16 text-center">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-md mb-6 animate-bounce">
              <CheckCircle2 className="w-9 h-9" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 font-sans tracking-tight">
              Order Placed
            </h2>

            <p className="text-m sm:text-base text-gray-700 mt-3 leading-relaxed">
              Thank you {orderName}, your order has been placed.
            </p>

            <p className="text-sm sm:text-sm text-gray-500 mt-1 leading-relaxed">
              {product?.brand} will contact you to verify your order details.
            </p>

            <div className="mt-8">
              <button
                type="button"
                onClick={() => setBuyModalView("closed")}
                className="px-8 py-3 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-semibold transition-all shadow-md cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductPage;
